import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import type { GlobalLauncherProps, LauncherSearchItem } from './types';
import { DEFAULT_LAUNCHER_LIMITS } from './types';
import { getItemAtIndex } from './rankLauncherResults';
import { ExperienceIcon, isRegistryKey } from '../icons';
import './global-launcher.css';

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

type UndoState = {
  type: 'remove' | 'clear';
  items: LauncherSearchItem[];
  timerId: ReturnType<typeof setTimeout>;
};

function LauncherResultItem({
  item,
  index,
  isActive,
  onSelect,
  onPinToggle,
  onMouseEnter,
}: {
  item: LauncherSearchItem;
  index: number;
  isActive: boolean;
  onSelect: (item: LauncherSearchItem) => void;
  onPinToggle: (item: LauncherSearchItem) => void;
  onMouseEnter: (index: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      id={`launcher-item-${index}`}
      role="option"
      aria-selected={isActive}
      className={`launcher-result-item ${isActive ? 'active' : ''}`}
      onClick={() => onSelect(item)}
      onMouseEnter={() => onMouseEnter(index)}
    >
      <span className="launcher-result-icon" aria-hidden="true">
        {isRegistryKey(item.icon) ? <ExperienceIcon iconKey={item.icon} size={18} /> : (item.icon || '\u2022')}
      </span>
      <div className="launcher-result-content">
        <span className="launcher-result-title">{item.title}</span>
        {item.subtitle && (
          <span className="launcher-result-subtitle">{item.subtitle}</span>
        )}
        {item.description && (
          <span className="launcher-result-description">{item.description}</span>
        )}
      </div>
      <button
        className={`launcher-pin-btn ${item.pinned ? 'pinned' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onPinToggle(item);
        }}
        aria-label={item.pinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
        tabIndex={0}
      >
        {item.pinned ? '\u2605' : '\u2606'}
      </button>
    </div>
  );
}

function RecentResultItem({
  item,
  index,
  isActive,
  onSelect,
  onRemove,
  onMouseEnter,
}: {
  item: LauncherSearchItem;
  index: number;
  isActive: boolean;
  onSelect: (item: LauncherSearchItem) => void;
  onRemove: (pluginId: string) => void;
  onMouseEnter: (index: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      id={`launcher-item-${index}`}
      role="option"
      aria-selected={isActive}
      className={`launcher-result-item launcher-recent-item ${isActive ? 'active' : ''}`}
      onClick={() => onSelect(item)}
      onMouseEnter={() => onMouseEnter(index)}
    >
      <span className="launcher-result-icon" aria-hidden="true">
        {isRegistryKey(item.icon) ? <ExperienceIcon iconKey={item.icon} size={18} /> : (item.icon || '\u2022')}
      </span>
      <div className="launcher-result-content">
        <span className="launcher-result-title">{item.title}</span>
        <span className="launcher-recent-meta">
          {item.lastAccessedAt && formatRelativeTime(item.lastAccessedAt)}
          {item.accessCount && item.accessCount > 1 && (
            <span className="launcher-recent-count">
              {' \u00B7 opened '}{item.accessCount}{' times'}
            </span>
          )}
        </span>
      </div>
      <button
        className="launcher-remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        aria-label={`Remove ${item.title} from recents`}
        tabIndex={0}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

function LauncherSection({
  title,
  items,
  startIndex,
  activeIndex,
  onSelect,
  onPinToggle,
  onMouseEnter,
}: {
  title: string;
  items: LauncherSearchItem[];
  startIndex: number;
  activeIndex: number;
  onSelect: (item: LauncherSearchItem) => void;
  onPinToggle: (item: LauncherSearchItem) => void;
  onMouseEnter: (index: number) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="launcher-section">
      <div className="launcher-section-title">{title}</div>
      {items.map((item, i) => (
        <LauncherResultItem
          key={item.id}
          item={item}
          index={startIndex + i}
          isActive={activeIndex === startIndex + i}
          onSelect={onSelect}
          onPinToggle={onPinToggle}
          onMouseEnter={onMouseEnter}
        />
      ))}
    </div>
  );
}

function RecentSection({
  items,
  startIndex,
  activeIndex,
  onSelect,
  onRemove,
  onClear,
  onMouseEnter,
}: {
  items: LauncherSearchItem[];
  startIndex: number;
  activeIndex: number;
  onSelect: (item: LauncherSearchItem) => void;
  onRemove: (pluginId: string) => void;
  onClear: () => void;
  onMouseEnter: (index: number) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="launcher-section">
      <div className="launcher-section-header">
        <span className="launcher-section-title">Recent</span>
        <button
          className="launcher-clear-btn"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          aria-label="Clear all recent items"
        >
          Clear
        </button>
      </div>
      {items.map((item, i) => (
        <RecentResultItem
          key={item.id}
          item={item}
          index={startIndex + i}
          isActive={activeIndex === startIndex + i}
          onSelect={onSelect}
          onRemove={onRemove}
          onMouseEnter={onMouseEnter}
        />
      ))}
    </div>
  );
}

function ZeroResultState({ query }: { query: string }) {
  return (
    <div className="launcher-zero-results">
      <div className="launcher-zero-icon" aria-hidden="true">&#x1F50D;</div>
      <div className="launcher-zero-text">
        No matches for &ldquo;{query}&rdquo;
      </div>
      <div className="launcher-zero-hint">
        Try different words or check the mega menu for browsing.
      </div>
    </div>
  );
}

function KeyboardHints() {
  return (
    <div className="launcher-footer">
      <span className="launcher-hint">
        <kbd>&uarr;</kbd><kbd>&darr;</kbd> Navigate
      </span>
      <span className="launcher-hint">
        <kbd>Enter</kbd> Open
      </span>
      <span className="launcher-hint">
        <kbd>Esc</kbd> Close
      </span>
    </div>
  );
}

export function GlobalLauncher({
  open,
  sections,
  query,
  selectedIndex,
  totalResultCount,
  onQueryChange,
  onSelect,
  onPinToggle,
  onRemoveRecent,
  onClearRecents,
  onRestoreRecent,
  onClose,
  triggerRef,
}: GlobalLauncherProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [undoState, setUndoState] = useState<UndoState | null>(null);

  // Focus input on open; return focus to trigger on close
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    } else if (triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [open, triggerRef]);

  // Clear undo state when launcher closes
  useEffect(() => {
    if (!open && undoState) {
      clearTimeout(undoState.timerId);
      setUndoState(null);
    }
  }, [open, undoState]);

  const [hoverIndex, setHoverIndex] = useState(-1);
  const effectiveIndex = hoverIndex >= 0 ? hoverIndex : selectedIndex;

  const handleMouseEnter = useCallback((index: number) => {
    setHoverIndex(index);
  }, []);

  // Reset hover when keyboard navigation happens
  useEffect(() => {
    setHoverIndex(-1);
  }, [selectedIndex]);

  // Escape to close + Enter to select (using effectiveIndex) + focus trap
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key === 'Enter' && effectiveIndex >= 0) {
        e.preventDefault();
        e.stopPropagation();
        const item = getItemAtIndex(sections, effectiveIndex);
        if (item) onSelect(item);
        return;
      }

      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onClose, effectiveIndex, sections, onSelect]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleRemoveRecent = useCallback((pluginId: string) => {
    const item = sections.recent.find(i => i.id === pluginId);
    if (!item || !onRemoveRecent) return;

    // Store for undo
    if (undoState) clearTimeout(undoState.timerId);
    const timerId = setTimeout(() => setUndoState(null), DEFAULT_LAUNCHER_LIMITS.undoTimeoutMs);
    setUndoState({ type: 'remove', items: [item], timerId });

    onRemoveRecent(pluginId);
  }, [sections.recent, onRemoveRecent, undoState]);

  const handleClearRecents = useCallback(() => {
    if (!onClearRecents || sections.recent.length === 0) return;

    if (undoState) clearTimeout(undoState.timerId);
    const timerId = setTimeout(() => setUndoState(null), DEFAULT_LAUNCHER_LIMITS.undoTimeoutMs);
    setUndoState({ type: 'clear', items: [...sections.recent], timerId });

    onClearRecents();
  }, [onClearRecents, sections.recent, undoState]);

  const handleUndo = useCallback(() => {
    if (!undoState || !onRestoreRecent) return;
    clearTimeout(undoState.timerId);
    for (const item of undoState.items) {
      onRestoreRecent(item);
    }
    setUndoState(null);
  }, [undoState, onRestoreRecent]);

  const activeDescendant = effectiveIndex >= 0
    ? `launcher-item-${effectiveIndex}`
    : undefined;

  const hasResults = totalResultCount > 0;
  const hasQuery = query.trim().length > 0;

  const sectionOffsets = useMemo(() => {
    let offset = 0;
    const offsets = {
      pinned: offset,
      recent: (offset += sections.pinned.length),
      bestMatch: (offset += sections.recent.length),
      otherMatches: (offset += sections.bestMatch.length),
      actions: (offset += sections.otherMatches.length),
    };
    return offsets;
  }, [sections]);

  if (!open) return null;

  const showEmptyState = !hasQuery && sections.pinned.length === 0 && sections.recent.length === 0 && sections.bestMatch.length === 0;

  const dialog = (
    <div
      className="launcher-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search or jump to anything"
        className="launcher-panel"
      >
        {/* Search input */}
        <div className="launcher-search-row">
          <svg className="launcher-search-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11.5 11.5L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="launcher-search-input"
            placeholder="Search or jump to anything..."
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            aria-label="Search plugins, actions, and settings"
            aria-describedby="launcher-status"
            aria-controls="launcher-results"
            aria-activedescendant={activeDescendant}
            aria-expanded={hasResults}
            role="combobox"
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="launcher-shortcut-hint">Esc</kbd>
        </div>

        {/* Live region for screen readers */}
        <div
          id="launcher-status"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="launcher-sr-only"
        >
          {hasQuery && (
            totalResultCount === 0
              ? 'No results found'
              : `${totalResultCount} result${totalResultCount === 1 ? '' : 's'} available`
          )}
        </div>

        {/* Results */}
        <div
          id="launcher-results"
          role="listbox"
          aria-label="Search results"
          className="launcher-results"
        >
          {showEmptyState && (
            <div className="launcher-empty-state">
              <div className="launcher-empty-heading">Jump back in</div>
              <p className="launcher-empty-text">
                Your recent and pinned experiences appear here.
              </p>
            </div>
          )}

          <LauncherSection
            title="Pinned"
            items={sections.pinned}
            startIndex={sectionOffsets.pinned}
            activeIndex={effectiveIndex}
            onSelect={onSelect}
            onPinToggle={onPinToggle}
            onMouseEnter={handleMouseEnter}
          />

          <RecentSection
            items={sections.recent}
            startIndex={sectionOffsets.recent}
            activeIndex={effectiveIndex}
            onSelect={onSelect}
            onRemove={handleRemoveRecent}
            onClear={handleClearRecents}
            onMouseEnter={handleMouseEnter}
          />

          <LauncherSection
            title={hasQuery ? 'Best match' : 'Recommended'}
            items={sections.bestMatch}
            startIndex={sectionOffsets.bestMatch}
            activeIndex={effectiveIndex}
            onSelect={onSelect}
            onPinToggle={onPinToggle}
            onMouseEnter={handleMouseEnter}
          />

          <LauncherSection
            title="Other matches"
            items={sections.otherMatches}
            startIndex={sectionOffsets.otherMatches}
            activeIndex={effectiveIndex}
            onSelect={onSelect}
            onPinToggle={onPinToggle}
            onMouseEnter={handleMouseEnter}
          />

          <LauncherSection
            title="Actions"
            items={sections.actions}
            startIndex={sectionOffsets.actions}
            activeIndex={effectiveIndex}
            onSelect={onSelect}
            onPinToggle={onPinToggle}
            onMouseEnter={handleMouseEnter}
          />

          {hasQuery && !hasResults && (
            <ZeroResultState query={query} />
          )}
        </div>

        {/* Undo snackbar */}
        {undoState && (
          <div className="launcher-undo-bar" role="alert">
            <span className="launcher-undo-text">
              {undoState.type === 'remove' ? 'Removed from recents.' : 'Recent items cleared.'}
            </span>
            <button className="launcher-undo-btn" onClick={handleUndo}>
              Undo
            </button>
          </div>
        )}

        <KeyboardHints />
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}

export default GlobalLauncher;
